import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import AnnouncementBody from '../components/AnnouncementBody';
import {
  useListActiveAnnouncementsQuery,
  useLoadCompanyDirectoryQuery,
  useGetAdminConfigQuery,
  useGetCurrentComplianceBulletinQuery,
  useListJobOpeningsQuery,
} from '../app/api';
import { useIsMobile } from './utils';
import onThisDay from '../data/onThisDay.json';
import nationalDays from '../data/nationalDays.json';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

// Quick Links tile. Add a row here rather than a new dashboard card when someone
// asks for something to be "on the hub" — that's the point of the tile. The
// Compliance Quick Hitter is rendered separately above these because it opens in
// a new tab, and it hides itself when nothing is published.
const QUICK_LINKS = [
  { label: 'Employee Directory', to: '/employee-directory', icon: 'people-fill' },
  { label: 'Holidays',           to: '/holidays',           icon: 'calendar-heart-fill' },
  // Label and icon match the sidebar item, and so does the gating: hidden unless
  // the employee has at least one posting they're allowed to see, otherwise the
  // row leads to an empty page.
  { label: 'Career Opportunities', to: '/job-openings', icon: 'briefcase',
    requiresContent: 'jobOpenings' },
];

// Social accounts, grouped because the advisor-facing and client-facing pages are
// DIFFERENT audiences — the grouping is there so nobody shares the advisor page
// to clients by mistake. Entries with a null url are skipped by the tile, so a
// missing URL degrades to "not shown" rather than a dead link.
const SOCIAL_GROUPS = [
  {
    label: 'Advisor Network',
    links: [
      { name: 'Facebook', icon: 'facebook', url: 'https://www.facebook.com/ProsperityAdvisorNetwork' },
      { name: 'LinkedIn', icon: 'linkedin', url: 'https://www.linkedin.com/company/prosperity-advisor-network' },
    ],
  },
  {
    label: 'Client Facing',
    links: [
      { name: 'Facebook', icon: 'facebook', url: 'https://www.facebook.com/ProsperityCapitalAdvisors' },
      { name: 'LinkedIn', icon: 'linkedin', url: 'https://www.linkedin.com/company/prosperity-capital-advisors' },
    ],
  },
];

// Shared by every row, so the bulletin row (which opens a new tab) is visually
// indistinguishable from the rows that navigate in place.
// minHeight keeps every row the same height whether it carries one line of text
// or two — the Quick Hitter row adds the period underneath its label, which would
// otherwise make it taller than its neighbours.
const QUICK_LINK_MIN_HEIGHT = '3.25rem';
const quickLinkStyle = {
  display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%',
  minHeight: QUICK_LINK_MIN_HEIGHT, height: '100%',
  background: '#f7f6f4', border: '1px solid rgba(10,41,71,0.10)', borderRadius: 8,
  padding: '0.6rem 0.75rem', textAlign: 'left', textDecoration: 'none',
  color: '#0a2947', fontSize: '0.86rem', cursor: 'pointer',
};

// "Model Team Member" card content. Each section has a heading + subtitle and a
// list of items; an item is either a plain string or a [term, description] pair
// (rendered as a bolded term followed by its description).
// `column` splits the card into two internal columns: Core Values and the AI
// Native Standard stack on the left, Performance Multipliers takes the right on
// its own. Grouping them this way keeps the two columns roughly level — the
// multipliers carry a description per item, so it is about as tall as the other
// two combined.
const MODEL_SECTIONS = [
  {
    heading: 'Core Values',
    sub: 'Who We Are',
    column: 1,
    items: [
      'People First',
      'Innovation 2 Evolution',
      'Ownership Mindset',
      'Hungry, Yet Humble',
      'Positive, Grateful, Fun',
    ],
  },
  {
    heading: 'AI Native Standard',
    sub: 'How We Operate',
    column: 1,
    items: [],
  },
  {
    heading: 'Performance Multipliers',
    sub: 'How We Win',
    column: 2,
    items: [
      ['Outcome-Accountable', 'Deliver results that matter'],
      ['Execution Excellence', 'High-quality follow-through'],
      ['Resourceful Problem Solver', 'Find solutions'],
      ['Rapid Adapter', 'Learn and adjust'],
      ['Performance Multiplier', 'Elevate others'],
    ],
  },
];

// A single-line, horizontally-scrollable strip: no wrapping (so the banner's
// height never grows with the item count), scrollbar hidden via
// .scroll-x-hidden, with fade + click-to-scroll arrows appearing only on
// whichever edge still has hidden content.
function HScrollStrip({ items, bg, arrowColor, children }) {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft > 4;
      const right = el.scrollWidth - el.clientWidth - el.scrollLeft > 4;
      setOverflow(prev => (prev.left === left && prev.right === right) ? prev : { left, right });
    };
    update();
    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [items]);

  const scrollBy = (dir) => ref.current?.scrollBy({ left: dir * 220, behavior: 'smooth' });

  const arrowStyle = (side) => ({
    position: 'absolute', [side]: 0, top: '50%', transform: 'translateY(-50%)',
    width: 24, height: 24, boxSizing: 'border-box', borderRadius: '50%', border: `2px solid ${brand.goldenrod}`,
    background: 'rgba(255,255,255,0.9)', color: arrowColor,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', boxShadow: '0 1px 4px rgba(10,41,71,0.3)', padding: 0,
  });

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <div ref={ref} className="scroll-x-hidden" style={{ display: 'flex', flexWrap: 'nowrap', gap: '0.4rem', overflowX: 'auto' }}>
        {children}
      </div>
      {overflow.left && (
        <>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 36, background: `linear-gradient(90deg, ${bg}, transparent)`, pointerEvents: 'none' }} />
          <button type="button" aria-label="Scroll left" onClick={() => scrollBy(-1)} style={arrowStyle('left')}>
            <i className="bi bi-chevron-left" style={{ fontSize: '0.75rem' }} />
          </button>
        </>
      )}
      {overflow.right && (
        <>
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: `linear-gradient(270deg, ${bg}, transparent)`, pointerEvents: 'none' }} />
          <button type="button" aria-label="Scroll right" onClick={() => scrollBy(1)} style={arrowStyle('right')}>
            <i className="bi bi-chevron-right" style={{ fontSize: '0.75rem' }} />
          </button>
        </>
      )}
    </div>
  );
}

HScrollStrip.propTypes = {
  items: PropTypes.array.isRequired,
  bg: PropTypes.string.isRequired,
  arrowColor: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export default function Dashboard() {
  const isMobile = useIsMobile();
  const { data: announcements = [], isLoading: announcementsLoading } = useListActiveAnnouncementsQuery();
  // Still needed for the birthday/anniversary strips even though the full
  // directory now lives on its own page at /employee-directory.
  const { data: directoryRows = [] } = useLoadCompanyDirectoryQuery();
  const { data: adminConfigData } = useGetAdminConfigQuery();
  const { data: currentBulletin } = useGetCurrentComplianceBulletinQuery();
  const birthdayHorizonDays = adminConfigData?.config?.birthday_lookahead_days ?? 14;
  const anniversaryHorizonDays = adminConfigData?.config?.anniversary_lookahead_days ?? 14;

  // Same query the sidebar already runs (the server filters by each posting's
  // visibility tier), so RTK serves this from cache rather than refetching.
  const jobOpenings = useListJobOpeningsQuery();
  const hasViewableJobOpenings = (jobOpenings.data?.openings?.length || 0) > 0;
  const quickLinks = QUICK_LINKS.filter(
    link => link.requiresContent !== 'jobOpenings' || hasViewableJobOpenings
  );

  // Birthdays within the next 14 days. dob is stored as month/day only so we
  // compute days-until against today, rolling forward to next year if the date
  // has already passed this year.
  const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const upcomingBirthdays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const list = [];
    for (const r of directoryRows) {
      if (!r.dob_month || !r.dob_day) continue;
      const year = today.getFullYear();
      let bday = new Date(year, r.dob_month - 1, r.dob_day);
      bday.setHours(0, 0, 0, 0);
      if (bday < today) {
        bday = new Date(year + 1, r.dob_month - 1, r.dob_day);
        bday.setHours(0, 0, 0, 0);
      }
      const daysUntil = Math.round((bday - today) / 86400000);
      if (daysUntil <= birthdayHorizonDays) {
        list.push({ emp: r, daysUntil });
      }
    }
    list.sort((a, b) => a.daysUntil - b.daysUntil);
    return list;
  }, [directoryRows, birthdayHorizonDays]);

  const labelForDays = (d, emp) => {
    if (d === 0) return 'Today';
    if (d === 1) return 'Tomorrow';
    return `${MONTH_ABBR[emp.dob_month]} ${emp.dob_day}`;
  };

  // Work anniversaries within the next 14 days. hire_date is a full date, so
  // (unlike dob) we can compute the exact milestone year, not just the date.
  const upcomingAnniversaries = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const list = [];
    for (const r of directoryRows) {
      if (!r.hire_date) continue;
      // hire_date is a bare "YYYY-MM-DD" string — new Date(str) parses that as
      // UTC midnight, and the local getMonth()/getDate() below would then read
      // back a day early in any timezone behind UTC. Force local-time parsing
      // instead (same fix already used in EmployeeDirectory.js/HRHolidays.js).
      const hire = new Date(r.hire_date + 'T00:00:00');
      if (Number.isNaN(hire.getTime())) continue;
      const thisYear = today.getFullYear();
      let anniv = new Date(thisYear, hire.getMonth(), hire.getDate());
      anniv.setHours(0, 0, 0, 0);
      if (anniv < today) {
        anniv = new Date(thisYear + 1, hire.getMonth(), hire.getDate());
        anniv.setHours(0, 0, 0, 0);
      }
      const years = anniv.getFullYear() - hire.getFullYear();
      if (years < 1) continue;
      const daysUntil = Math.round((anniv - today) / 86400000);
      if (daysUntil <= anniversaryHorizonDays) {
        list.push({ emp: r, daysUntil, years, anniv });
      }
    }
    list.sort((a, b) => a.daysUntil - b.daysUntil);
    return list;
  }, [directoryRows, anniversaryHorizonDays]);

  const labelForAnniv = (entry) => {
    if (entry.daysUntil === 0) return 'Today';
    if (entry.daysUntil === 1) return 'Tomorrow';
    return entry.anniv.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const todaysTidbit = useMemo(() => {
    const now = new Date();
    const key = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const entry = onThisDay[key];
    const nationalDay = nationalDays[key] || null;
    if (!entry && !nationalDay) return null;
    return {
      key,
      year: entry?.year ?? null,
      event: entry?.event ?? null,
      yearsAgo: entry ? now.getFullYear() - entry.year : null,
      nationalDay,
      dateLabel: now.toLocaleDateString(undefined, { month: 'long', day: 'numeric' }),
    };
  }, []);

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>

      {/* Upcoming birthdays — full-bleed gold header. Negative margins escape
          the page padding so it sits edge-to-edge. Kept to a single row via
          HScrollStrip so the banner's height stays fixed regardless of how
          many fall in the window. */}
      {upcomingBirthdays.length > 0 && (
        <div style={{
          background: brand.goldenrod,
          color: brand.deepBlue,
          padding: isMobile ? '0.6rem 0.85rem' : '0.65rem 1.25rem',
          marginTop: isMobile ? '-0.75rem' : '-1.5rem',
          marginLeft: isMobile ? '-0.75rem' : '-1.5rem',
          marginRight: isMobile ? '-0.75rem' : '-1.5rem',
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.6rem' : '0.85rem',
          boxShadow: '0 2px 6px rgba(10,41,71,0.15)',
        }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <i className="bi bi-cake2-fill" style={{ fontSize: '1.1rem' }} />
            <span style={{ fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Birthdays
            </span>
          </div>
          <HScrollStrip items={upcomingBirthdays} bg={brand.goldenrod} arrowColor={brand.deepBlue}>
            {upcomingBirthdays.map(({ emp, daysUntil }) => {
              const isToday = daysUntil === 0;
              const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.final_email || 'Employee';
              return (
                <span
                  key={emp.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    background: isToday ? brand.deepBlue : 'rgba(255,255,255,0.55)',
                    color: isToday ? 'white' : brand.deepBlue,
                    borderRadius: 999,
                    padding: '0.2rem 0.65rem',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isToday && <i className="bi bi-balloon-heart-fill" style={{ fontSize: '0.8rem' }} />}
                  <span>{name}</span>
                  <span style={{
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    opacity: isToday ? 1 : 0.7,
                  }}>
                    {labelForDays(daysUntil, emp)}
                  </span>
                </span>
              );
            })}
          </HScrollStrip>
        </div>
      )}

      {/* Upcoming work anniversaries — same pill-strip idea as birthdays, but
          kept to a single, static-height row with horizontal scroll instead
          of wrapping, so a busy window doesn't grow the banner. */}
      {upcomingAnniversaries.length > 0 && (
        <div style={{
          background: brand.tealBlue,
          color: 'white',
          borderRadius: '10px',
          padding: isMobile ? '0.6rem 0.85rem' : '0.65rem 1.25rem',
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.6rem' : '0.85rem',
          boxShadow: '0 2px 6px rgba(10,41,71,0.15)',
        }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <i className="bi bi-trophy-fill" style={{ fontSize: '1.1rem', color: brand.goldenrod }} />
            <span style={{ fontWeight: 800, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Anniversaries
            </span>
          </div>
          <HScrollStrip items={upcomingAnniversaries} bg={brand.tealBlue} arrowColor={brand.tealBlue}>
            {upcomingAnniversaries.map((entry) => {
              const { emp, years, daysUntil } = entry;
              const isToday = daysUntil === 0;
              const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.final_email || 'Employee';
              return (
                <span
                  key={emp.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    background: isToday ? brand.deepBlue : 'rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: 999,
                    padding: '0.2rem 0.65rem',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isToday && <i className="bi bi-award-fill" style={{ fontSize: '0.8rem', color: brand.goldenrod }} />}
                  <span>{name}</span>
                  <span style={{
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    letterSpacing: '0.5px',
                    background: 'rgba(255,255,255,0.25)',
                    borderRadius: 999,
                    padding: '0.05rem 0.4rem',
                  }}>
                    {years}yr
                  </span>
                  <span style={{ fontSize: '0.65rem', opacity: 0.85 }}>{labelForAnniv(entry)}</span>
                </span>
              );
            })}
          </HScrollStrip>
        </div>
      )}

      {/* Model Team Member sidebar + a stacked right column. The sidebar is tall
          (three stacked sections), and announcements alone left a big gap beneath
          it, so Quick Links stacks under announcements to fill that column rather
          than sitting in its own row further down the page. */}
      <div className="row g-3" style={{ marginBottom: '1.25rem' }}>
        <div className="col-12 col-lg-4">
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="bi bi-star-fill" style={{ color: brand.goldenrod }} />
              <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Model Team Member</h5>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              {/* Floated right so the Core Values bullets sit to its left and the
                  sections below flow up naturally instead of being pushed down by
                  the image's height. */}
              <img
                src="/model-team-member.png"
                alt="Model team member"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                style={{ float: 'right', width: 130, marginLeft: '0.9rem', marginBottom: '0.4rem', maxHeight: 200, objectFit: 'contain' }}
              />
              {MODEL_SECTIONS.map(sec => (
                <div key={sec.heading} style={{ marginBottom: '0.9rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', flexWrap: 'wrap', marginBottom: sec.items.length ? '0.25rem' : 0 }}>
                    <span style={{ fontWeight: 800, color: brand.deepBlue, fontSize: '0.95rem' }}>{sec.heading}</span>
                    <span style={{ color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{sec.sub}</span>
                  </div>
                  {sec.items.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                      {sec.items.map((it, i) => {
                        const [term, desc] = Array.isArray(it) ? it : [it, null];
                        return (
                          <li key={i} style={{ color: brand.charcoal, fontSize: '0.85rem', lineHeight: 1.45, marginBottom: 2 }}>
                            <span style={{ fontWeight: 700, color: brand.deepBlue }}>{term}</span>{desc ? ` — ${desc}` : ''}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
              <div style={{ clear: 'both' }} />
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden' }}>
            <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="bi bi-megaphone" style={{ color: brand.goldenrod }} />
              <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Company Announcements</h5>
            </div>
            <div style={{ padding: '1rem 1.25rem', maxHeight: 380, overflowY: 'auto' }}>
              {announcementsLoading && (
                <p style={{ color: brand.charcoal, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>Loading…</p>
              )}
              {!announcementsLoading && announcements.map(ann => (
                <div key={ann.id} style={{ padding: '0.85rem 1rem', borderRadius: '8px', background: ann.pinned ? 'rgba(10,41,71,0.04)' : 'transparent', border: ann.pinned ? `1px solid rgba(10,41,71,0.1)` : '1px solid transparent', marginBottom: '0.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '4px' }}>
                    {ann.pinned && <i className="bi bi-pin-fill" style={{ color: brand.goldenrod, fontSize: '0.75rem' }} />}
                    <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.9rem' }}>{ann.title}</span>
                  </div>
                  {/* maxWidth keeps the measure readable now the tile is full
                      width — without it a one-line announcement stretches the
                      whole page and becomes hard to scan. */}
                  <AnnouncementBody
                    style={{ color: brand.charcoal, fontSize: '0.875rem', lineHeight: 1.5, maxWidth: '78ch' }}
                    linkColor={brand.tealBlue}
                  >
                    {ann.body || ''}
                  </AnnouncementBody>
                </div>
              ))}
              {!announcementsLoading && announcements.length === 0 && (
                <p style={{ color: brand.charcoal, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>No announcements at this time.</p>
              )}
            </div>
          </div>
          <div>
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', height: '100%' }}>
            <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="bi bi-link-45deg" style={{ color: brand.goldenrod }} />
              <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Quick Links</h5>
            </div>
            <div style={{ padding: isMobile ? '0.75rem' : '1rem 1.25rem' }}>
              <div className="row g-2">
                {currentBulletin && (
                  <div className="col-12 col-md-6">
                    <a
                      href={`/compliance/quick-hitter/view/${currentBulletin.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={quickLinkStyle}
                    >
                      <i className="bi bi-clipboard2-check-fill" style={{ color: brand.goldenrod, fontSize: '1.1rem' }} />
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                        <span style={{ fontWeight: 600 }}>Compliance Quick Hitter</span>
                        {currentBulletin.period_label && (
                          <span style={{ fontSize: '0.72rem', color: brand.charcoal }}>
                            {currentBulletin.period_label}
                          </span>
                        )}
                      </span>
                      <i className="bi bi-box-arrow-up-right"
                         style={{ color: brand.charcoal, fontSize: '0.75rem', marginLeft: 'auto' }} />
                    </a>
                  </div>
                )}
                {quickLinks.map(link => (
                  <div className="col-12 col-md-6" key={link.to}>
                    <Link to={link.to} style={quickLinkStyle}>
                      <i className={`bi bi-${link.icon}`} style={{ color: brand.goldenrod, fontSize: '1.1rem' }} />
                      <span style={{ fontWeight: 600 }}>{link.label}</span>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* "On this day" — moved below Company Announcements. */}
      {todaysTidbit && (
        <div className="row g-3" style={{ marginBottom: '1.25rem' }}>
          <div className="col-12 col-lg-8">
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: brand.tealBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="bi bi-hourglass-split" style={{ color: brand.goldenrod }} />
                <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>On This Day</h5>
              </div>
              {todaysTidbit.event && (
                <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, color: brand.deepBlue, fontSize: '1.6rem', lineHeight: 1 }}>{todaysTidbit.year}</span>
                    <span style={{ color: brand.charcoal, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                      {todaysTidbit.yearsAgo} {todaysTidbit.yearsAgo === 1 ? 'year' : 'years'} ago · {todaysTidbit.dateLabel}
                    </span>
                  </div>
                  <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.5, margin: 0 }}>
                    {todaysTidbit.event}
                  </p>
                </div>
              )}
            </div>
          </div>
          {/* "Today is also" — styled to match the On This Day card. */}
          {todaysTidbit.nationalDay && (
            <div className="col-12 col-lg-4">
              <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: brand.tealBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <i className="bi bi-calendar-event-fill" style={{ color: brand.goldenrod }} />
                  <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Today Is Also</h5>
                </div>
                <div style={{ padding: '1rem 1.25rem' }}>
                  <span style={{ color: brand.deepBlue, fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.3 }}>
                    {todaysTidbit.nationalDay}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Follow Us — full-width strip at the foot of the page. At this width the
          message and the two audience groups sit side by side rather than
          stacking, which keeps the tile short. */}
      <div className="row g-3">
        <div className="col-12">
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden' }}>
            <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="bi bi-share-fill" style={{ color: brand.goldenrod }} />
              <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Follow Us</h5>
            </div>
            <div style={{ padding: isMobile ? '0.75rem' : '1rem 1.25rem', display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: 0, lineHeight: 1.45, flex: '1 1 300px', maxWidth: '60ch' }}>
                Like, share and follow to help extend our reach — every share puts
                our content in front of people we could not otherwise reach.
              </p>
              {SOCIAL_GROUPS.map(group => {
                const available = group.links.filter(l => l.url);
                if (available.length === 0) return null;
                return (
                  <div key={group.label} style={{ flex: '0 0 auto' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: brand.charcoal, marginBottom: '0.35rem' }}>
                      {group.label}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {available.map(l => (
                        <a
                          key={l.url}
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`${group.label} — ${l.name}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                            background: brand.softGray, color: brand.deepBlue,
                            border: '1px solid rgba(10,41,71,0.12)', borderRadius: 6,
                            padding: '0.35rem 0.6rem', fontSize: '0.78rem',
                            fontWeight: 600, textDecoration: 'none',
                          }}
                        >
                          <i className={`bi bi-${l.icon}`} style={{ color: brand.tealBlue }} />
                          {l.name}
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
